/**
 * Minting a reCAPTCHA v3 token, when there is one to mint.
 *
 * ## The two things that shape this
 *
 * **The script is loaded on the screens that use it, never on boot.** reCAPTCHA v3 attaches an
 * observer to the page for the whole of a session, and loading it the moment anybody opens the
 * application would mean every screen — a rent roll, a tenant's file — is watched by Google so
 * that the sign-in form, which that person passed twenty minutes ago, can be scored.
 *
 * So a screen that has a protected form on it calls `primeRecaptcha()` as it mounts, and no other
 * screen loads anything at all. Priming on mount rather than on the first press is what makes the
 * badge appear **with** the form instead of a beat after somebody has already pressed Send — the
 * badge is the only visible sign that v3 is there at all, and one that arrives after the fact
 * reads as something having gone wrong. It also takes Google's round trip off the submit path.
 *
 * **Nothing here is allowed to stop somebody signing in.** If the key is not configured, if the
 * script will not load, if Google is slow — every one of those returns no token and the request
 * goes without one. The server decides what to do about a missing token, and it is the server that
 * knows whether a captcha is required at all. A browser that refuses to send the form because a
 * third-party script failed has turned somebody else's outage into ours.
 *
 * The site key is fetched from our own API rather than built into the bundle, so rotating it or
 * turning the whole thing off is an environment variable and a restart. See `lib/recaptcha.ts` in
 * the API, and ADR-0094.
 */

/** The actions the API knows about. Keeping them in step is what makes the replay check work. */
export type RecaptchaAction =
  | "sign_in"
  | "sign_up"
  | "forgot_password"
  | "reset_password"
  | "second_factor"
  | "portal_link"
  | "payment_claim";

declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
    };
  }
}

const SCRIPT_TIMEOUT_MS = 6000;

/** Asked for once per page, then remembered — including the answer "there is no key". */
let configPromise: Promise<string | null> | null = null;
let scriptPromise: Promise<boolean> | null = null;

async function siteKey(): Promise<string | null> {
  configPromise ??= fetch("/api/v1/auth/recaptcha/config", { credentials: "include" })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload: { enabled?: boolean; siteKey?: string | null } | null) =>
      payload?.enabled && payload.siteKey ? payload.siteKey : null,
    )
    .catch(() => null);
  return configPromise;
}

function loadScript(key: string): Promise<boolean> {
  scriptPromise ??= new Promise<boolean>((resolve) => {
    if (window.grecaptcha) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.append(script);

    /* A script tag that never fires either event — a blocked request, a captive portal — would
       otherwise leave every form waiting on a promise that never settles. */
    window.setTimeout(() => resolve(Boolean(window.grecaptcha)), SCRIPT_TIMEOUT_MS);
  });
  return scriptPromise;
}

/**
 * Fetch the key and load the script, without minting anything.
 *
 * Called by `<RecaptchaNotice />` as a protected form mounts. It answers nothing and throws
 * nothing: if the captcha is off, or Google cannot be reached, this quietly does no work and the
 * form behaves exactly as it did before.
 */
export function primeRecaptcha(): void {
  void siteKey().then((key) => (key ? loadScript(key) : false));
}

/** Whether a captcha is configured at all — for a notice that should not appear when it is not. */
export async function recaptchaConfigured(): Promise<boolean> {
  return Boolean(await siteKey());
}

/**
 * A token for this action, or null.
 *
 * Null is a perfectly ordinary answer and every caller must treat it as one: it means the captcha
 * is switched off, or Google could not be reached. Only the server decides whether that is
 * acceptable.
 */
export async function recaptchaToken(action: RecaptchaAction): Promise<string | null> {
  try {
    const key = await siteKey();
    if (!key) return null;
    if (!(await loadScript(key))) return null;
    if (!window.grecaptcha) return null;

    await new Promise<void>((resolve) => window.grecaptcha!.ready(resolve));
    return await window.grecaptcha.execute(key, { action });
  } catch {
    return null;
  }
}

/** The header the API reads, ready to spread into a request. Empty when there is no token. */
export async function recaptchaHeader(action: RecaptchaAction): Promise<Record<string, string>> {
  const token = await recaptchaToken(action);
  return token ? { "x-recaptcha-token": token } : {};
}
